/**
 * Staff Wiki - Quick Reference Guide for Consultation Calls
 * Powerful search and categorization for instant information access
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { wikiData, wikiCategories, searchWiki } from '../../../data/wikiData';

const StaffWiki = () => {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('countries');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
    } else {
      const results = searchWiki(searchQuery);
      setSearchResults(results);
    }
  }, [searchQuery]);

  // Get current category data
  const currentData = useMemo(() => {
    if (searchQuery) return null;
    return wikiData[activeCategory] || null;
  }, [activeCategory, searchQuery]);

  // Render country details
  const renderCountryDetails = (countryCode, country) => (
    <div className="bg-gray-800 rounded-lg shadow-md p-6 space-y-6 border border-gray-700">
      <div className="flex items-center gap-4 pb-4 border-b border-gray-700">
        <span className="text-5xl">{country.flag}</span>
        <div>
          <h3 className="text-2xl font-bold text-white">{country.name}</h3>
          <p className="text-sm text-gray-400">Comprehensive guide for staff consultations</p>
        </div>
      </div>

      {/* Popular Programs */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <span className="text-xl">🎓</span> Popular Programs
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {country.popularPrograms.map((program, idx) => (
            <div key={idx} className="bg-blue-900/20 p-3 rounded-lg border border-blue-800">
              <span className="text-sm font-medium text-blue-400">{program}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Key Features */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <span className="text-xl">⭐</span> Key Features
        </h4>
        <ul className="space-y-2">
          {country.keyFeatures.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="text-green-400 mt-1">✓</span>
              <span className="text-gray-300">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Requirements */}
      {country.requirements && (
        <div>
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <span className="text-xl">✅</span> Requirements
          </h4>
          <div className="space-y-4">
            {Object.entries(country.requirements).map(([level, req]) => (
              <div key={level} className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                <h5 className="font-semibold text-white mb-2 capitalize">{level.replace(/([A-Z])/g, ' $1').trim()}</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {req.academic && (
                    <div>
                      <span className="text-xs font-semibold text-gray-400 uppercase">Academic</span>
                      <p className="text-sm text-gray-300">{req.academic}</p>
                    </div>
                  )}
                  {req.language && (
                    <div>
                      <span className="text-xs font-semibold text-gray-400 uppercase">Language</span>
                      <p className="text-sm text-gray-300">{req.language}</p>
                    </div>
                  )}
                  {req.workExp && (
                    <div>
                      <span className="text-xs font-semibold text-gray-400 uppercase">Work Experience</span>
                      <p className="text-sm text-gray-300">{req.workExp}</p>
                    </div>
                  )}
                  {req.additional && (
                    <div className="md:col-span-2">
                      <span className="text-xs font-semibold text-gray-400 uppercase">Additional</span>
                      <ul className="text-sm text-gray-300 mt-1">
                        {req.additional.map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="text-blue-400">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {req.ieltsNote && (
                    <div className="md:col-span-2 bg-yellow-900/20 p-2 rounded border border-yellow-800">
                      <p className="text-xs text-yellow-400">
                        <span className="font-semibold">Note:</span> {req.ieltsNote}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Costs */}
      {country.costs && (
        <div>
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <span className="text-xl">💰</span> Cost Breakdown
          </h4>
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-3 border border-gray-700">
            {Object.entries(country.costs).map(([key, value]) => (
              <div key={key} className="flex justify-between items-start">
                <span className="text-sm font-medium text-gray-300 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}:
                </span>
                <span className="text-sm text-white text-right">
                  {typeof value === 'object' ? (
                    <div className="space-y-1">
                      {Object.entries(value).map(([pkg, price]) => (
                        <div key={pkg}>
                          <span className="font-semibold uppercase text-xs text-gray-400">{pkg}:</span> {price}
                        </div>
                      ))}
                    </div>
                  ) : (
                    value
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Financial Support */}
      {country.financialSupport && (
        <div className="bg-green-900/20 border border-green-800 p-4 rounded-lg">
          <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center gap-2">
            <span className="text-xl">💳</span> Study Loan Assistance
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs font-semibold text-green-500 uppercase">Coverage</span>
              <p className="text-sm text-green-300">{country.financialSupport.coverage}</p>
            </div>
            <div>
              <span className="text-xs font-semibold text-green-500 uppercase">Repayment</span>
              <p className="text-sm text-green-300">{country.financialSupport.repayment}</p>
            </div>
            <div>
              <span className="text-xs font-semibold text-green-500 uppercase">Interest</span>
              <p className="text-sm text-green-300">{country.financialSupport.interest}</p>
            </div>
            <div>
              <span className="text-xs font-semibold text-green-500 uppercase">Monthly Payment</span>
              <p className="text-sm text-green-300">{country.financialSupport.monthlyPayment}</p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {country.timeline && (
        <div>
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <span className="text-xl">⏱️</span> Timeline
          </h4>
          <p className="text-gray-300 bg-blue-900/20 p-3 rounded-lg border border-blue-800">
            {typeof country.timeline === 'string' ? country.timeline : country.timeline.bachelor}
          </p>
        </div>
      )}
    </div>
  );

  // Render package details
  const renderPackageDetails = (packageCode, pkg) => (
    <div className="bg-gray-800 rounded-lg shadow-md p-6 space-y-6 border border-gray-700">
      <div className="pb-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white">{pkg.name}</h3>
            <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-400">{pkg.price}</div>
            <div className="text-xs text-gray-500 mt-1">
              {pkg.refundable ? `✓ ${pkg.refundable}` : '✗ Non-refundable'}
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <span className="text-xl">📋</span> Features Included
        </h4>
        <ul className="space-y-2">
          {pkg.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="text-green-400 mt-1">✓</span>
              <span className="text-gray-300">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Ideal For */}
      <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-800">
        <h4 className="text-lg font-semibold text-blue-400 mb-2">Ideal For</h4>
        <p className="text-sm text-blue-300">{pkg.idealFor}</p>
      </div>

      {/* Countries Coverage */}
      <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-semibold text-white mb-2">Countries Coverage</h4>
        <p className="text-sm text-gray-300">{pkg.countries}</p>
      </div>

      {/* Payment Plan */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <span className="text-xl">💳</span> Payment Plan
        </h4>
        <div className="bg-gray-900/50 p-4 rounded-lg space-y-2 border border-gray-700">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-300">Upfront:</span>
            <span className="text-sm text-white">{pkg.paymentPlan.upfront}</span>
          </div>
          {pkg.paymentPlan.afterOffer !== '0' && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-300">After Offer:</span>
              <span className="text-sm text-white">{pkg.paymentPlan.afterOffer}</span>
            </div>
          )}
          {pkg.paymentPlan.final !== '0' && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-300">Final Payment:</span>
              <span className="text-sm text-white">{pkg.paymentPlan.final}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render search results
  const renderSearchResults = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-white">
        Search Results ({searchResults.length})
      </h3>
      {searchResults.length === 0 ? (
        <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-8 text-center">
          <p className="text-yellow-400">No results found. Try different keywords.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {searchResults.map((result, idx) => (
            <div
              key={idx}
              className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                if (result.type === 'country') {
                  setActiveCategory('countries');
                  setSelectedCountry(result.countryCode);
                } else if (result.type === 'package') {
                  setActiveCategory('packages');
                  setSelectedPackage(result.packageCode);
                }
                setSearchQuery('');
                setSearchResults([]);
              }}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">{result.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase">{result.section}</span>
                    <span className="text-xs text-gray-600">•</span>
                    <span className="text-xs text-gray-500 capitalize">{result.type}</span>
                  </div>
                  <h4 className="font-semibold text-white mb-1">{result.title}</h4>
                  <p className="text-sm text-gray-400">{result.content}</p>
                </div>
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-purple-900 rounded-lg shadow-lg p-6 text-white border border-blue-800 mb-6">
        <h1 className="text-3xl font-bold mb-2">📚 Staff Wiki</h1>
        <p className="text-blue-200">
          Quick reference guide for consultation calls - Instant access to country info, packages, requirements, and more
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search countries, packages, requirements, costs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-700 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg placeholder-gray-500"
        />
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery('');
              setSearchResults([]);
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <svg className="h-5 w-5 text-gray-500 hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search Results */}
      {searchQuery && renderSearchResults()}

      {!searchQuery && (
        <>
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {Object.values(wikiCategories).map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setActiveCategory(category.id);
                  setSelectedCountry(null);
                  setSelectedPackage(null);
                }}
                className={`
                  px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2
                  ${activeCategory === category.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                  }
                `}
              >
                <span>{category.icon}</span>
                <span>{category.title}</span>
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="min-h-[500px]">
            {/* Countries Category */}
            {activeCategory === 'countries' && (
              <div className="space-y-6">
                {!selectedCountry ? (
                  <>
                    <h2 className="text-2xl font-bold text-white mb-4">Countries & Programs</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(wikiData.countries).map(([code, country]) => (
                        <div
                          key={code}
                          onClick={() => setSelectedCountry(code)}
                          className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:shadow-lg transition-all cursor-pointer group"
                        >
                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-4xl">{country.flag}</span>
                            <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                              {country.name}
                            </h3>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm text-gray-400">
                              <span className="font-semibold">Programs:</span> {country.popularPrograms.length} available
                            </p>
                            <p className="text-sm text-gray-400">
                              <span className="font-semibold">Key Feature:</span> {country.keyFeatures[0]}
                            </p>
                            <div className="mt-3 text-blue-400 font-medium text-sm group-hover:text-blue-300">
                              Click to view details →
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div>
                    <button
                      onClick={() => setSelectedCountry(null)}
                      className="mb-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back to Countries
                    </button>
                    {renderCountryDetails(selectedCountry, wikiData.countries[selectedCountry])}
                  </div>
                )}
              </div>
            )}

            {/* Packages Category */}
            {activeCategory === 'packages' && (
              <div className="space-y-6">
                {!selectedPackage ? (
                  <>
                    <h2 className="text-2xl font-bold text-white mb-4">Packages & Pricing</h2>
                    <div className="grid gap-6">
                      {Object.entries(wikiData.packages).map(([code, pkg]) => (
                        <div
                          key={code}
                          onClick={() => setSelectedPackage(code)}
                          className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:shadow-lg transition-all cursor-pointer group"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                                {pkg.name}
                              </h3>
                              <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-2xl font-bold text-blue-400">{pkg.price}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {pkg.refundable ? `✓ ${pkg.refundable}` : '✗ Non-refundable'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-400">
                              <span className="font-semibold">Coverage:</span> {pkg.countries}
                            </div>
                            <div className="text-blue-400 font-medium text-sm group-hover:text-blue-300">
                              View details →
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div>
                    <button
                      onClick={() => setSelectedPackage(null)}
                      className="mb-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back to Packages
                    </button>
                    {renderPackageDetails(selectedPackage, wikiData.packages[selectedPackage])}
                  </div>
                )}
              </div>
            )}

            {/* Other Categories */}
            {['eligibility', 'financial', 'process', 'documents'].includes(activeCategory) && (
              <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {wikiCategories[activeCategory].title}
                </h2>
                <div className="prose max-w-none">
                  {activeCategory === 'eligibility' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3">Common Requirements</h3>
                        <ul className="space-y-2">
                          {wikiData.eligibility.commonRequirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-green-400 mt-1">✓</span>
                              <span className="text-gray-300">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3">By Country</h3>
                        <div className="space-y-4">
                          {Object.entries(wikiData.eligibility.byCountry).map(([country, requirements]) => (
                            <div key={country} className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                              <h4 className="font-semibold text-white mb-2 capitalize">{country}</h4>
                              <ul className="space-y-1">
                                {Array.isArray(requirements) ? (
                                  requirements.map((req, idx) => (
                                    <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                                      <span className="text-blue-400">•</span>
                                      {req}
                                    </li>
                                  ))
                                ) : (
                                  Object.entries(requirements).map(([level, reqs]) => (
                                    <div key={level} className="mt-2">
                                      <h5 className="text-sm font-semibold text-gray-400 capitalize mb-1">{level}</h5>
                                      <ul className="space-y-1 ml-4">
                                        {reqs.map((req, idx) => (
                                          <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                                            <span className="text-blue-400">•</span>
                                            {req}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))
                                )}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeCategory === 'financial' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-800">
                          <h3 className="text-lg font-semibold text-blue-400 mb-2">Show Money</h3>
                          <p className="text-2xl font-bold text-blue-300 mb-1">{wikiData.financial.showMoney.minimum}</p>
                          <p className="text-sm text-blue-200">{wikiData.financial.showMoney.purpose}</p>
                          <p className="text-xs text-blue-300 mt-2">{wikiData.financial.showMoney.note}</p>
                        </div>
                        <div className="bg-green-900/20 p-4 rounded-lg border border-green-800">
                          <h3 className="text-lg font-semibold text-green-400 mb-2">Blocked Account (Germany)</h3>
                          <p className="text-2xl font-bold text-green-300 mb-1">{wikiData.financial.blockedAccount.germany.amount}</p>
                          <p className="text-sm text-green-200">{wikiData.financial.blockedAccount.germany.purpose}</p>
                          <p className="text-xs text-green-300 mt-2">{wikiData.financial.blockedAccount.germany.note}</p>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3">Consulting Fees</h3>
                        <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                          <div className="grid grid-cols-2 gap-4">
                            {Object.entries(wikiData.financial.consultingFees).map(([pkg, fee]) => (
                              <div key={pkg} className="flex justify-between">
                                <span className="text-sm font-medium text-gray-300 capitalize">{pkg}:</span>
                                <span className="text-sm font-semibold text-white">{fee}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      {wikiData.financial.studyLoans && (
                        <div className="bg-purple-900/20 p-4 rounded-lg border border-purple-800">
                          <h3 className="text-lg font-semibold text-purple-400 mb-3">Study Loans (Canada)</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-xs font-semibold text-purple-500 uppercase">Coverage</span>
                              <p className="text-sm text-purple-300">{wikiData.financial.studyLoans.canada.coverage}</p>
                            </div>
                            <div>
                              <span className="text-xs font-semibold text-purple-500 uppercase">Repayment</span>
                              <p className="text-sm text-purple-300">{wikiData.financial.studyLoans.canada.repayment}</p>
                            </div>
                            <div>
                              <span className="text-xs font-semibold text-purple-500 uppercase">Interest</span>
                              <p className="text-sm text-purple-300">{wikiData.financial.studyLoans.canada.interestRate}</p>
                            </div>
                            <div>
                              <span className="text-xs font-semibold text-purple-500 uppercase">Monthly Payment</span>
                              <p className="text-sm text-purple-300">{wikiData.financial.studyLoans.canada.monthlyPayment}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeCategory === 'process' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3">General Process</h3>
                        <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-800 mb-4">
                          <p className="text-sm text-blue-300 mb-2">
                            <span className="font-semibold">Total Timeline:</span> {wikiData.process.general.totalTimeline}
                          </p>
                        </div>
                        <ol className="space-y-2">
                          {wikiData.process.general.steps.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                                {idx + 1}
                              </span>
                              <span className="text-gray-300">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3">By Country</h3>
                        <div className="space-y-4">
                          {Object.entries(wikiData.process.byCountry).map(([country, data]) => (
                            <div key={country} className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                              <h4 className="font-semibold text-white mb-2 capitalize">{country}</h4>
                              <p className="text-sm text-gray-400 mb-2">
                                <span className="font-semibold">Timeline:</span> {data.timeline}
                              </p>
                              <p className="text-xs text-gray-500 mb-3">
                                <span className="font-semibold">Critical Path:</span> {data.criticalPath}
                              </p>
                              <ol className="space-y-1">
                                {data.steps.map((step, idx) => (
                                  <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                                    <span className="text-blue-400 font-semibold">{idx + 1}.</span>
                                    {step}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeCategory === 'documents' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3">General Documents</h3>
                        <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-800">
                          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {wikiData.documents.general.map((doc, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-blue-300">
                                <span className="text-blue-400 mt-1">•</span>
                                {doc}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(wikiData.documents).filter(([key]) => key !== 'general').map(([country, docs]) => (
                          <div key={country} className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                            <h4 className="font-semibold text-white mb-2 capitalize">{country.replace(/([A-Z])/g, ' $1').trim()} Specific</h4>
                            <ul className="space-y-1">
                              {docs.map((doc, idx) => (
                                <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                                  <span className="text-blue-400 mt-1">•</span>
                                  {doc}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* FAQ Category */}
            {activeCategory === 'faq' && (
              <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {wikiCategories[activeCategory].title}
                </h2>
                <div className="space-y-4">
                  {wikiData.faq.map((item, idx) => (
                    <div key={idx} className="border border-gray-700 rounded-lg overflow-hidden">
                      <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                        <div className="flex items-start gap-3">
                          <span className="text-xl">{wikiCategories[item.category]?.icon || '❓'}</span>
                          <h3 className="font-semibold text-white flex-1">{item.question}</h3>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-800">
                        <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Category */}
            {activeCategory === 'contact' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {wikiCategories[activeCategory].title}
                </h2>

                {/* Offices */}
                <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
                  <h3 className="text-xl font-semibold text-white mb-4">Our Offices</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {wikiData.contact.offices.map((office, idx) => (
                      <div key={idx} className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{office.flag}</span>
                          <h4 className="font-semibold text-white">{office.country}</h4>
                        </div>
                        <p className="text-sm text-gray-400 mb-1">{office.city}</p>
                        <p className="text-sm text-gray-400 mb-2">
                          <span className="font-semibold">Role:</span> {office.role}
                        </p>
                        <p className="text-xs text-gray-500 mb-2">{office.address}</p>
                        <p className="text-xs text-blue-400">{office.email}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Support Info */}
                <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
                  <h3 className="text-xl font-semibold text-white mb-4">Support Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-white mb-2">Business Hours</h4>
                      <p className="text-sm text-gray-300">{wikiData.contact.support.hours}</p>
                      <p className="text-xs text-gray-500 mt-2">{wikiData.contact.support.responseTime}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Emergency Support</h4>
                      <p className="text-sm text-gray-300">{wikiData.contact.support.emergency}</p>
                    </div>
                  </div>
                </div>

                {/* Departments */}
                <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
                  <h3 className="text-xl font-semibold text-white mb-4">Department Contacts</h3>
                  <div className="space-y-3">
                    {wikiData.contact.departments.map((dept, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-900/50 p-3 rounded-lg border border-gray-700">
                        <span className="font-medium text-white">{dept.name}</span>
                        <span className="text-sm text-blue-400">{dept.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Custom Email Creation Category */}
            {activeCategory === 'customEmail' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-2">
                  📧 {wikiData.customEmail.title}
                </h2>
                <p className="text-gray-400 mb-6">{wikiData.customEmail.description}</p>

                {/* Quick Access Button */}
                <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 rounded-lg p-6 border border-cyan-700">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-cyan-400 mb-1">Step 1: Open Register.lk</h3>
                      <p className="text-sm text-gray-300">Start here to access the hosting control panel</p>
                    </div>
                    <a
                      href={wikiData.customEmail.loginUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-lg"
                    >
                      <span>🔗</span>
                      Open Register.lk
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>

                {/* Login Credentials Card */}
                <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <span>🔐</span> Quick Reference - Credentials
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">Register.lk Login</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-20">Username:</span>
                          <code className="text-sm text-cyan-400 bg-gray-800 px-2 py-1 rounded">{wikiData.customEmail.credentials.username}</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-20">Password:</span>
                          <code className="text-sm text-cyan-400 bg-gray-800 px-2 py-1 rounded">{wikiData.customEmail.credentials.password}</code>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">New Student Email</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-20">Format:</span>
                          <code className="text-sm text-green-400 bg-gray-800 px-2 py-1 rounded">firstname.initial{wikiData.customEmail.emailDomain}</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-20">Password:</span>
                          <code className="text-sm text-green-400 bg-gray-800 px-2 py-1 rounded">{wikiData.customEmail.defaultPassword}</code>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step-by-Step Guide */}
                <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <span>📋</span> Step-by-Step Guide
                  </h3>
                  <div className="space-y-4">
                    {wikiData.customEmail.steps.map((step, idx) => (
                      <div
                        key={idx}
                        className={`relative pl-8 pb-4 ${idx !== wikiData.customEmail.steps.length - 1 ? 'border-l-2 border-gray-700' : ''}`}
                      >
                        {/* Step Number Circle */}
                        <div className="absolute left-0 -translate-x-1/2 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-lg">
                          {step.step}
                        </div>

                        <div className="ml-4 bg-gray-900/50 p-4 rounded-lg border border-gray-700 hover:border-cyan-700 transition-colors">
                          <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                          <p className="text-sm text-gray-400 mb-2">{step.description}</p>
                          <p className="text-sm text-cyan-400">{step.action}</p>

                          {/* Show credentials if present */}
                          {step.credentials && (
                            <div className="mt-3 bg-gray-800 p-3 rounded border border-gray-600">
                              <div className="text-xs space-y-1">
                                <div><span className="text-gray-500">Username:</span> <code className="text-cyan-400">{step.credentials.username}</code></div>
                                <div><span className="text-gray-500">Password:</span> <code className="text-cyan-400">{step.credentials.password}</code></div>
                              </div>
                            </div>
                          )}

                          {/* Show password if present */}
                          {step.password && (
                            <div className="mt-2">
                              <code className="text-green-400 bg-gray-800 px-2 py-1 rounded">{step.password}</code>
                            </div>
                          )}

                          {/* Show note if present */}
                          {step.note && (
                            <p className="mt-2 text-xs text-yellow-400 italic">💡 {step.note}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Important Notes */}
                <div className="bg-yellow-900/20 rounded-lg p-6 border border-yellow-800">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
                    <span>⚠️</span> Important Notes
                  </h3>
                  <ul className="space-y-2">
                    {wikiData.customEmail.notes.map((note, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-yellow-300">
                        <span className="text-yellow-500 mt-1">•</span>
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StaffWiki;